// src/components/ProjectBuilder/TaskViewOptions.tsx
import { useState } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore, ViewList, ViewModule, ViewComfy } from '@mui/icons-material';

export type SortBy = 'priority' | 'deadline' | 'project' | 'status' | 'airport';
export type SortOrder = 'asc' | 'desc';
export type GroupBy = 'none' | 'project' | 'airport' | 'user' | 'userType' | 'status';
export type Density = 'comfortable' | 'compact' | 'list';

export interface TaskViewOptionsProps {
  sortBy: SortBy;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  density: Density;
  onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void;
  onGroupChange: (groupBy: GroupBy) => void;
  onDensityChange: (density: Density) => void;
  userRole: 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User';
}

const STORAGE_KEY = 'taskViewOptions';

// Load saved preferences from localStorage
export function loadViewOptions(): {
  sortBy: SortBy;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  density: Density;
} {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        sortBy: parsed.sortBy || 'priority',
        sortOrder: parsed.sortOrder || 'desc',
        groupBy: parsed.groupBy || 'none',
        density: parsed.density || 'comfortable',
      };
    }
  } catch (error) {
    console.error('Error loading view options:', error);
  }
  return {
    sortBy: 'priority',
    sortOrder: 'desc',
    groupBy: 'none',
    density: 'comfortable',
  };
}

// Save preferences to localStorage
export function saveViewOptions(options: {
  sortBy: SortBy;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  density: Density;
}) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch (error) {
    console.error('Error saving view options:', error);
  }
}

export default function TaskViewOptions({
  sortBy,
  sortOrder,
  groupBy,
  density,
  onSortChange,
  onGroupChange,
  onDensityChange,
  userRole,
}: TaskViewOptionsProps) {
  const [expanded, setExpanded] = useState(false);
  const isManagerOrAdmin = userRole === 'System Admin' || userRole === 'Airport Manager';

  const handleSortByChange = (event: SelectChangeEvent<SortBy>) => {
    const newSortBy = event.target.value as SortBy;
    // Default sort order based on sort type
    let newSortOrder: SortOrder = 'desc';
    if (newSortBy === 'deadline' || newSortBy === 'project') {
      newSortOrder = 'asc';
    }
    onSortChange(newSortBy, newSortOrder);
    saveViewOptions({ sortBy: newSortBy, sortOrder: newSortOrder, groupBy, density });
  };

  const handleSortOrderChange = (event: SelectChangeEvent<SortOrder>) => {
    const newSortOrder = event.target.value as SortOrder;
    onSortChange(sortBy, newSortOrder);
    saveViewOptions({ sortBy, sortOrder: newSortOrder, groupBy, density });
  };

  const handleGroupByChange = (event: SelectChangeEvent<GroupBy>) => {
    const newGroupBy = event.target.value as GroupBy;
    onGroupChange(newGroupBy);
    saveViewOptions({ sortBy, sortOrder, groupBy: newGroupBy, density });
  };

  const handleDensityChange = (_event: React.MouseEvent<HTMLElement>, newDensity: Density | null) => {
    if (newDensity !== null) {
      onDensityChange(newDensity);
      saveViewOptions({ sortBy, sortOrder, groupBy, density: newDensity });
    }
  };

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
          View Options
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Sort Options */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={handleSortByChange}>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="deadline">Deadline</MenuItem>
                <MenuItem value="project">Project</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                {isManagerOrAdmin && <MenuItem value="airport">Airport</MenuItem>}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={handleSortOrderChange}>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Group By Options */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Group By</InputLabel>
            <Select value={groupBy} label="Group By" onChange={handleGroupByChange}>
              <MenuItem value="none">None (Flat List)</MenuItem>
              <MenuItem value="project">By Project</MenuItem>
              {isManagerOrAdmin && <MenuItem value="airport">By Airport</MenuItem>}
              {isManagerOrAdmin && <MenuItem value="user">By User</MenuItem>}
              {isManagerOrAdmin && <MenuItem value="userType">By User Type</MenuItem>}
              <MenuItem value="status">By Status</MenuItem>
            </Select>
          </FormControl>

          {/* Density Options */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Display Density
            </Typography>
            <ToggleButtonGroup
              value={density}
              exclusive
              onChange={handleDensityChange}
              size="small"
              aria-label="display density"
            >
              <ToggleButton value="comfortable" aria-label="comfortable">
                <ViewComfy sx={{ mr: 0.5 }} />
                Comfortable
              </ToggleButton>
              <ToggleButton value="compact" aria-label="compact">
                <ViewModule sx={{ mr: 0.5 }} />
                Compact
              </ToggleButton>
              <ToggleButton value="list" aria-label="list">
                <ViewList sx={{ mr: 0.5 }} />
                List
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

